import type { Role } from '../../../../core/policy/roles';

/**
 * Authentication port — owned by this feature, implemented in `infrastructure/`
 * (blueprint §3.4, §4.1).
 *
 * The feature never imports the Firebase SDK. That is not ceremony: it is what
 * lets every screen below be tested without a network, and what stops platform
 * types leaking into UI code where a future platform change would have to be
 * chased through every component.
 */

/** What a verified sign-in tells us. Nothing here is client-assertable. */
export interface AuthenticatedUser {
  readonly uid: string;
  readonly email: string | null;
  /** Google's own verification. An unverified address proves no domain. */
  readonly emailVerified: boolean;
  readonly displayName: string | null;
  /**
   * Roles from the token's custom claims, never from a document.
   * Empty until a manager grants one, and stale until the next sign-in.
   */
  readonly roles: readonly Role[];
}

export type SignInFailureCode =
  | 'popup_blocked'
  | 'popup_closed'
  | 'network'
  | 'wrong_domain'
  | 'unknown';

export class SignInError extends Error {
  // Declared explicitly rather than as a constructor parameter property:
  // `erasableSyntaxOnly` requires that stripping types leaves valid JavaScript,
  // and a parameter property has no JavaScript equivalent.
  readonly code: SignInFailureCode;

  constructor(code: SignInFailureCode, message: string) {
    super(message);
    this.name = 'SignInError';
    this.code = code;
  }
}

export interface AuthPort {
  /**
   * Starts Google sign-in.
   *
   * The domain hint passed to Google is a *convenience* that pre-selects the
   * right account. It is not a restriction — it travels from the browser and
   * anyone can change it. Domain enforcement lives in the security rules and in
   * `refuseSignInReason`, both of which run against the verified token.
   */
  signInWithGoogle(): Promise<AuthenticatedUser>;

  signOut(): Promise<void>;

  /**
   * Observes the current user. Returns an unsubscribe — §11.5.1 forbids a
   * listener without one, and an auth listener outlives every screen.
   */
  observeUser(onChange: (user: AuthenticatedUser | null) => void): () => void;

  /**
   * Forces a token refresh so newly-granted roles apply without a full
   * sign-out. Roles are only refreshed on request because a token is cached.
   */
  refreshRoles(): Promise<readonly Role[]>;
}
